import { Check, ArrowRight, Link2, BadgeCheck, BarChart3 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { FButton } from "@/components/common/FButton";
import { CTASection } from "@/components/common/CTASection";
import { StatusBadge } from "@/components/common/StatusBadge";
import { AMBASSADOR_TIERS } from "@/data/content";

const concepts = [
  { icon: Link2, title: "Referral code & link", body: "Every ambassador gets a personal code and link to share with creators who could be a fit." },
  { icon: BadgeCheck, title: "Verified referrals", body: "Referrals are tracked and verified. Rewards depend on eligibility conditions being met." },
  { icon: BarChart3, title: "Transparent tracking", body: "See your referrals clearly. No hidden math, no vague promises." },
];

const tierBorder = {
  grey: "border-white/10",
  teal: "border-ff-teal/40",
  ember: "border-ff-ember/40",
};

export default function Ambassadors() {
  return (
    <div data-testid="ambassadors-page">
      <PageHeader
        overline="Ambassador Program"
        titleLines={["Refer creators.", "Earn tracked rewards."]}
        lead="The FanFuel Ambassador Network helps people refer creators and earn tracked rewards when eligibility conditions are met."
        testid="ambassadors-header"
      >
        <FButton to="/apply?type=ambassador" data-testid="ambassadors-apply-btn">
          Apply as Ambassador <ArrowRight className="h-4 w-4" />
        </FButton>
      </PageHeader>

      {/* how it works concepts */}
      <section className="px-5 py-14 md:px-8 md:py-20">
        <div className="mx-auto max-w-7xl">
          <SectionHeading overline="How it works" title="Simple, transparent, verified." />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {concepts.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/8 bg-ff-slate/60 p-7">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ff-ember/15 text-ff-ember">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-display text-lg font-semibold text-ff-ivory">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ff-grey">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* tiers */}
      <section className="px-5 py-14 md:px-8 md:py-20">
        <div className="mx-auto max-w-7xl">
          <SectionHeading overline="Tiers" title="Grow with the network." />
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {AMBASSADOR_TIERS.map((tier, i) => (
              <Reveal key={tier.name} delay={i * 0.1}>
                <div
                  data-testid={`ambassador-tier-${i}`}
                  className={`relative flex h-full flex-col rounded-3xl border ${tierBorder[tier.accent]} bg-ff-slate/60 p-8 ${tier.featured ? "lg:-translate-y-3 shadow-[0_20px_60px_-20px_rgba(47,174,145,0.35)]" : ""}`}
                >
                  {tier.featured && (
                    <div className="absolute -top-3 left-8">
                      <StatusBadge tone="positive">Most active</StatusBadge>
                    </div>
                  )}
                  <span className="font-mono text-xs uppercase tracking-widest text-ff-grey">{tier.level}</span>
                  <h3 className="mt-3 font-display text-2xl font-bold text-ff-ivory">{tier.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ff-grey">{tier.body}</p>
                  <ul className="mt-6 space-y-3 border-t border-white/5 pt-6">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-ff-ivory">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-ff-teal" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* eligibility note */}
      <section className="px-5 pb-6 md:px-8">
        <Reveal className="mx-auto max-w-4xl rounded-3xl border border-ff-amber/25 bg-ff-amber/[0.06] p-8 md:p-10">
          <h3 className="font-display text-xl font-semibold text-ff-ivory md:text-2xl">
            No promise of commission until eligibility is met.
          </h3>
          <p className="mt-3 text-base leading-relaxed text-ff-grey">
            Rewards are earned only when a referral is verified and eligibility conditions are met. There
            are no guaranteed commissions — everything is tracked transparently.
          </p>
        </Reveal>
      </section>

      <CTASection
        title="Ready to join the network?"
        primaryLabel="Apply as Ambassador"
        primaryTo="/apply?type=ambassador"
        secondaryLabel="Read the FAQ"
        secondaryTo="/faq"
        showTrust={false}
      />
    </div>
  );
}
